import { AdapterRegistry } from 'app/model/adapters/adapter-registry';
import { Model } from 'app/model/backendmodel';
import { IModel } from '../../base/annotation';
import { Metamodel } from 'app/model/base/model';
import { AmazonS3LinkedService, s3AuthType } from 'app/model/linkedservice/amazon-s3.linkedservice';
import { LinkedServiceAdapter } from 'app/model/adapters/linkedservices/linkedservice.adapter';
import { ValueAdapter } from 'app/model/adapters/value.adapter';
import {
    parameterizablePasswordOrAKVtoSecretBase,
    parameterizableSecretBaseToModel,
    parameterizableTextOrAKVtoValue,
    parameterizableValueToModel
} from 'app/model/adapters/linkedservices/azure-key-vault.util';

export const adapts = {
    name: '*',
    properties: {
        description: '*',
        type: '*',
        connectVia: '*',
        typeProperties: {
            serviceUrl: '*',
            // Do not add this as a func, otherwise accessKeyId will be put into raw after being processed in ValueAdapter.remainder
            accessKeyId: 'akv',
            secretAccessKey: 'secret',
            encryptedCredential: '*',
            authenticationType: '*',
            sessionToken: 'secret'
        },
        parameters: '*'
    }
};

@AdapterRegistry.adapter({
    model: Metamodel.modelFor(AmazonS3LinkedService),
    asynchronous: true,
    adapts
})
export class AmazonS3LinkedServiceAdapter extends LinkedServiceAdapter<AmazonS3LinkedService> {
    protected supportFirstClassParameterExperience = true;

    protected modelConstructor(): new () => AmazonS3LinkedService {
        return AmazonS3LinkedService;
    }

    protected model(): IModel {
        return AmazonS3LinkedService.MODEL;
    }

    protected async propertiesToResourceAsync(
        linkedService: AmazonS3LinkedService,
        linkedServiceResource: Model.LinkedServiceResource
    ): Promise<Model.LinkedServiceResource> {
        const properties = this.properties(linkedServiceResource) as Model.AmazonS3LinkedService;

        properties.type = this.model().name;
        properties.typeProperties = {
            serviceUrl: this.valueToResource(linkedService, AmazonS3LinkedService.SERVICE_URL),
            accessKeyId: parameterizableTextOrAKVtoValue(
                linkedService,
                AmazonS3LinkedService.ACCESS_KEY_ID,
                this.context
            ),
            secretAccessKey: parameterizablePasswordOrAKVtoSecretBase(
                linkedService,
                AmazonS3LinkedService.ACCESS_KEY,
                this.context
            )
        };

        properties.typeProperties.authenticationType = linkedService.authType;

        if (linkedService.authType === s3AuthType.temporarySecurity) {
            properties.typeProperties.sessionToken = parameterizablePasswordOrAKVtoSecretBase(
                linkedService,
                AmazonS3LinkedService.SESSION_TOKEN,
                this.context
            );
        }

        return linkedServiceResource;
    }

    protected async propertiesToModelAsync(
        linkedServiceResource: Model.LinkedServiceResource,
        linkedService: AmazonS3LinkedService
    ): Promise<AmazonS3LinkedService> {
        const properties = linkedServiceResource.properties as Model.AmazonS3LinkedService;
        ValueAdapter.toModel(linkedService, AmazonS3LinkedService.SERVICE_URL, properties.typeProperties.serviceUrl);
        parameterizableValueToModel(
            linkedService,
            AmazonS3LinkedService.ACCESS_KEY_ID,
            properties.typeProperties.accessKeyId,
            this.context
        );
        parameterizableSecretBaseToModel(
            linkedService,
            AmazonS3LinkedService.ACCESS_KEY,
            properties.typeProperties.secretAccessKey,
            this.context
        );

        linkedService.authType = properties.typeProperties.authenticationType || s3AuthType.accessKey; // default to accessKey

        if (linkedService.authType === s3AuthType.temporarySecurity) {
            parameterizableSecretBaseToModel(
                linkedService,
                AmazonS3LinkedService.SESSION_TOKEN,
                properties.typeProperties.sessionToken,
                this.context
            );
        }

        return linkedService;
    }
}
